import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { InputValidation } from "../../../utils/InputValidationRegex";
import Theme from "../../../utils/Theme";
import AppButton from "../AppButton.component";
import { useAuth } from "../../../context/Auth.context";
import { appUrl } from "../../../utils/Utils";
import {
  SEND_EMAIL_ARTIFACT,
  SEND_TEXT_ARTIFACT,
} from "../../../graphql/mutation/mutation";
import { useMutation } from "@apollo/client";
import { CheckCircle } from "@mui/icons-material";

// Channel tags appended to the consent URL so the consent page knows
// how the customer reached it (used by knowledgehub_open_log on open).
// Must stay in sync with VALID_KH_CHANNELS in Consent.component.jsx and
// ALLOWED_CHANNELS in the logKnowledgeHubOpen resolver — all three are strings.
const CHANNEL = {
  EMAIL: "email",
  TEXT: "text",
  DEVICE: "device",
};

const buildConsentUrl = ({
  employeeUserId,
  dealershipId,
  customerUserId,
  walkthrough,
  channel,
}) =>
  appUrl +
  `/verification/consent/${employeeUserId}/${dealershipId}/${customerUserId}/${walkthrough}/0/${false}?service=2&channel=${channel}`;

const KnowledgeHubComponent = ({ customer }) => {
  console.log("KnowledgeHubComponent customer: ", customer);
  const auth = useAuth();
  const [isText, setIsText] = useState(false);

  const [
    sendEmailArtifact,
    { loading: artifactEmailLoading, data: emailArtifactData },
  ] = useMutation(SEND_EMAIL_ARTIFACT);
  const [
    sendTextArtifact,
    { loading: artifactTextLoading, data: textArtifactData },
  ] = useMutation(SEND_TEXT_ARTIFACT);

  const isWalkthrough = auth?.user?.associateDetails?.find(
    (dealership) => dealership?.dealership_id === auth?.user?.dealership_id
  );
  console.log("getWalkthroughVideo: ", isWalkthrough?.walkthrough_video);
  let getWalkthroughVideo =
    isWalkthrough?.walkthrough_video?.video_url ||
    isWalkthrough?.walkthrough_video?.es_video_url ||
    isWalkthrough?.walkthrough_video?.text ||
    isWalkthrough?.walkthrough_video?.es_text ||
    isWalkthrough?.walkthrough_video?.knowledgehub_video_url ||
    isWalkthrough?.walkthrough_video?.es_knowledgehub_video_url ||
    isWalkthrough?.walkthrough_video?.knowledgehub_text ||
    isWalkthrough?.walkthrough_video?.es_knowledgehub_text
      ? true
      : false;

  const emailhandler = () => {
    setIsText(false);
    sendEmailArtifact({
      variables: {
        verificationRequestInput: {
          user: {
            user_id: customer?.customer?.user_id,
            first_name: customer.customer?.first_name,
            middle_name: " ",
            last_name: customer?.customer?.last_name,
            email: customer?.contact?.email,
            phone_number: customer?.contact?.mobile_number,
          },
          employee: {
            employee_user_id: auth.user.user_id.toString(),
            employee_firstname: auth.user.first_name,
            employee_lastname: auth.user.last_name,
          },
          url: buildConsentUrl({
            employeeUserId: auth?.user?.user_id,
            dealershipId: auth?.user?.dealership_id,
            customerUserId: customer?.customer.user_id,
            walkthrough: getWalkthroughVideo,
            channel: CHANNEL.EMAIL,
          }),
          dealership: {
            dealership_name: auth.user.dealership_name,
          },
          isRepull: false,
        },
      },
    });
  };

  const textHandler = () => {
    setIsText(true);
    sendTextArtifact({
      variables: {
        verificationRequestInput: {
          user: {
            user_id: customer?.customer?.user_id,
            first_name: customer?.customer?.first_name,
            middle_name: " ",
            last_name: customer?.customer?.last_name,
            email: customer?.contact?.email,
            phone_number: customer?.contact?.mobile_number,
          },
          employee: {
            employee_user_id: auth.user.user_id.toString(),
            employee_firstname: auth.user.first_name,
            employee_lastname: auth.user.last_name,
          },
          url: buildConsentUrl({
            employeeUserId: auth?.user?.user_id,
            dealershipId: auth?.user?.dealership_id,
            customerUserId: customer?.customer.user_id,
            walkthrough: getWalkthroughVideo,
            channel: CHANNEL.TEXT,
          }),
          dealership: {
            dealership_name: auth.user.dealership_name,
          },
          isRepull: false,
        },
      },
    });
  };

  return (
    <>
      <Divider />
      <Box
        sx={{
          m: window.innerWidth <= 750 ? "5px" : "8px 10px 0px 15px",
        }}
      >
        <Box sx={{ display: "flex", gap: "25px", flexDirection: "column" }}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              color={
                InputValidation.nameValidation(customer.customer?.first_name)
                  ? "error"
                  : "primary"
              }
            >
              First Name *
            </InputLabel>
            <OutlinedInput
              required
              label="First Name"
              value={customer.customer?.first_name}
              sx={{ borderRadius: "6px" }}
              error={InputValidation.nameValidation(
                customer.customer?.first_name
              )}
            />
          </FormControl>

          <FormControl variant="outlined" fullWidth>
            <InputLabel
              color={
                InputValidation.nameValidation(customer.customer?.last_name)
                  ? "error"
                  : "primary"
              }
            >
              Last Name *
            </InputLabel>
            <OutlinedInput
              required
              label="Last Name"
              value={customer.customer?.last_name}
              sx={{ borderRadius: "6px" }}
              error={InputValidation.nameValidation(
                customer.customer?.last_name
              )}
            />
          </FormControl>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              color={
                InputValidation.emailValidation(customer?.contact?.email)
                  ? "error"
                  : "primary"
              }
            >
              Email *
            </InputLabel>
            <OutlinedInput
              required
              label="Email"
              value={customer?.contact?.email}
              sx={{ borderRadius: "6px" }}
              error={InputValidation.emailValidation(customer?.contact?.email)}
            />
          </FormControl>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              color={
                InputValidation.numberValidation(
                  customer.contact?.mobile_number
                )
                  ? "error"
                  : "primary"
              }
            >
              Mobile Number *
            </InputLabel>
            <OutlinedInput
              required={true}
              label="Mobile Number"
              value={InputValidation.phoneNumberFormat(
                customer.contact?.mobile_number
              )}
              sx={{ borderRadius: "6px" }}
              error={InputValidation.numberValidation(
                customer.contact?.mobile_number
              )}
            />
          </FormControl>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 1,
          }}
        >
          <AppButton
            variant="text"
            buttonText="Continue here"
            size="medium"
            clickHandler={() => {
              window.open(
                buildConsentUrl({
                  employeeUserId: auth?.user?.user_id,
                  dealershipId: auth?.user?.dealership_id,
                  customerUserId: customer?.customer.user_id,
                  walkthrough: getWalkthroughVideo,
                  channel: CHANNEL.DEVICE,
                }),
                "_blank"
              );
            }}
            disabled={artifactEmailLoading || artifactTextLoading}
          />
        </Box>
        <Divider />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            p: 2,
          }}
        >
          {textArtifactData?.sendTextArtifact?.status ? (
            <Box
              sx={{
                display: "flex",
                my: "14px",
                width: "49%",
                justifyContent: "center",
              }}
            >
              <CheckCircle color={"primary"} />
              <span
                style={{
                  color: Theme.palette.primary.main,
                  marginLeft: "4px",
                }}
              >
                TEXT SENT
              </span>
            </Box>
          ) : (
            <AppButton
              variant={isText ? "contained" : "outlined"}
              buttonText="Send Text"
              sx={{ width: "48%" }}
              clickHandler={textHandler}
              disabled={artifactEmailLoading || artifactTextLoading}
              loading={artifactTextLoading}
            />
          )}

          {emailArtifactData?.sendEmailArtifact?.status ? (
            <Box
              sx={{
                display: "flex",
                my: "14px",
                width: "49%",
                justifyContent: "center",
              }}
            >
              <CheckCircle color={"primary"} />
              <span
                style={{
                  color: Theme.palette.primary.main,
                  marginLeft: "4px",
                }}
              >
                EMAIL SENT
              </span>
            </Box>
          ) : (
            <AppButton
              variant={!isText ? "contained" : "outlined"}
              buttonText="Send Email"
              sx={{ width: "48%" }}
              clickHandler={emailhandler}
              disabled={artifactEmailLoading || artifactTextLoading}
              loading={artifactEmailLoading}
            />
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            px: 2,
            pb: 1,
          }}
        >
          <Typography variant="body" fontSize={"12px"} color={"grey"}>
            By selecting Send, you confirm that the customer has authorized
            the dealership to send this communication by the selected method.
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default KnowledgeHubComponent;